import { Button } from "design-system/atoms/Button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { cn } from "design-system/utils"
import { ChevronDown, Minus, Plus, X } from "lucide-react"
import { useLaunchAudienceLabel } from "modules/campaigns/hooks/useLaunchAudienceLabel"
import { TagsPicker } from "modules/customers/components/Tags/TagsPicker"
import { useMarketingChannelOptions } from "modules/customers/hooks/useMarketingChannelOptions"
import { useTagsQuery } from "modules/customers/hooks/useTagsQuery"
import { useIntegrationsStatusQuery } from "modules/settings/hooks/useIntegrationsStatusQuery"
import { useTranslation } from "react-i18next"
import { CampaignLaunchAudienceMatchEnum } from "schema"
import {
  AudienceRuleDraft,
  AudienceRuleKind,
  AudienceRulesDraft,
  AudienceRulesMatch,
} from "modules/campaigns/lib/launchAudienceRules"
import { SegmentsPicker } from "./SegmentsPicker"

type Props = {
  value: AudienceRulesDraft
  onChange: (value: AudienceRulesDraft) => void
}

export function AudienceRulesBuilder({ value, onChange }: Props) {
  const { t } = useTranslation()

  const includeSeparator =
    value.match === CampaignLaunchAudienceMatchEnum.All
      ? t("campaigns.launchAudience.label.and")
      : t("campaigns.launchAudience.label.or")

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-3 sm:p-4">
      {value.include.length > 1 ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span>{t("campaigns.launchAudience.matchPrefix")}</span>
          <Select
            value={value.match}
            onValueChange={(match) => onChange({ ...value, match: match as AudienceRulesMatch })}
          >
            <SelectTrigger size="sm" className="w-auto">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CampaignLaunchAudienceMatchEnum.All}>
                {t("campaigns.launchAudience.match.all")}
              </SelectItem>
              <SelectItem value={CampaignLaunchAudienceMatchEnum.Any}>
                {t("campaigns.launchAudience.match.any")}
              </SelectItem>
            </SelectContent>
          </Select>
          <span>{t("campaigns.launchAudience.matchSuffix")}</span>
        </div>
      ) : (
        <span className="text-sm">{t("campaigns.launchAudience.customersWho")}</span>
      )}

      <RuleList
        rules={value.include}
        separator={includeSeparator}
        isLastRuleLocked
        onChange={(include) => onChange({ ...value, include })}
      />

      {value.exclude.length > 0 && (
        <div className="flex flex-col gap-2 border-t pt-3">
          <span className="text-sm">{t("campaigns.launchAudience.exceptCustomersWho")}</span>
          <RuleList
            rules={value.exclude}
            separator={t("campaigns.launchAudience.label.or")}
            isException
            onChange={(exclude) => onChange({ ...value, exclude })}
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onChange({ ...value, include: [...value.include, { kind: "segments", ids: [] }] })
          }
        >
          <Plus />
          {t("campaigns.launchAudience.addRule")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onChange({ ...value, exclude: [...value.exclude, { kind: "tags", ids: [] }] })
          }
        >
          <Minus />
          {t("campaigns.launchAudience.addException")}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">{t("campaigns.launchAudience.rulesHint")}</p>
    </div>
  )
}

type RuleListProps = {
  rules: AudienceRuleDraft[]
  separator: string
  isLastRuleLocked?: boolean
  isException?: boolean
  onChange: (rules: AudienceRuleDraft[]) => void
}

function RuleList({ rules, separator, isLastRuleLocked, isException, onChange }: RuleListProps) {
  return (
    <div className="flex flex-col">
      {rules.map((rule, index) => (
        <div key={index} className="flex flex-col">
          {index > 0 && (
            <div className="flex items-center gap-2 pl-4">
              <span className="h-3 w-px bg-border" />
              <span className="rounded-md bg-secondary px-2 py-0.5 font-mono text-xs uppercase">
                {separator}
              </span>
            </div>
          )}
          <RuleRow
            rule={rule}
            isException={isException}
            isRemovable={!isLastRuleLocked || rules.length > 1}
            onChange={(next) => onChange(rules.map((item, i) => (i === index ? next : item)))}
            onRemove={() => onChange(rules.filter((_, i) => i !== index))}
          />
        </div>
      ))}
    </div>
  )
}

type RuleRowProps = {
  rule: AudienceRuleDraft
  isException?: boolean
  isRemovable: boolean
  onChange: (rule: AudienceRuleDraft) => void
  onRemove: () => void
}

function RuleRow({ rule, isException, isRemovable, onChange, onRemove }: RuleRowProps) {
  const { t } = useTranslation()
  const { data: tags = [] } = useTagsQuery()
  const { data: integrationsStatus } = useIntegrationsStatusQuery()
  const marketingChannelOptions = useMarketingChannelOptions()
  const { describeSegments, describeTags, describeChannels } = useLaunchAudienceLabel()

  const kindConfig = {
    segments: {
      valueLabel: describeSegments(rule.ids),
      placeholder: t("campaigns.launchAudience.pickSegments"),
      verb: t("campaigns.launchAudience.isInAnyOf"),
    },
    tags: {
      valueLabel: describeTags(rule.ids),
      placeholder: t("campaigns.launchAudience.pickTags"),
      verb: t("campaigns.launchAudience.hasAnyOf"),
    },
    subscribedTo: {
      valueLabel: describeChannels(rule.ids),
      placeholder: t("campaigns.launchAudience.pickChannels"),
      verb: t("campaigns.launchAudience.isSubscribedToAnyOf"),
    },
  }[rule.kind]

  function toggle(id: string) {
    onChange({
      ...rule,
      ids: rule.ids.includes(id) ? rule.ids.filter((item) => item !== id) : [...rule.ids, id],
    })
  }

  const pickerTrigger = (
    <Button variant="outline" className="min-w-0 flex-1 justify-between font-normal">
      <span className={cn(!kindConfig.valueLabel && "text-muted-foreground")}>
        {kindConfig.valueLabel || kindConfig.placeholder}
      </span>
      <ChevronDown className="text-muted-foreground" />
    </Button>
  )

  function renderPicker() {
    switch (rule.kind) {
      case "segments":
        return (
          <SegmentsPicker selectedValues={rule.ids} onChange={(ids) => onChange({ ...rule, ids })}>
            {pickerTrigger}
          </SegmentsPicker>
        )
      case "tags":
        return (
          <TagsPicker
            options={tags.map((tag) => ({ value: tag.id, label: tag.name }))}
            selectedValues={rule.ids}
            onToggle={toggle}
          >
            {pickerTrigger}
          </TagsPicker>
        )
      case "subscribedTo":
        return (
          <TagsPicker
            options={marketingChannelOptions}
            selectedValues={rule.ids}
            onToggle={toggle}
            searchPlaceholder={t("campaigns.launchAudience.searchChannels")}
          >
            {pickerTrigger}
          </TagsPicker>
        )
    }
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-lg border bg-card p-2.5 shadow-xs sm:flex-row sm:items-center",
        isException && "border-dashed border-input",
      )}
    >
      <div className="flex items-center gap-2">
        <Select
          value={rule.kind}
          onValueChange={(kind) => onChange({ kind: kind as AudienceRuleKind, ids: [] })}
        >
          <SelectTrigger size="sm" className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="segments">{t("campaigns.launchAudience.kind.segments")}</SelectItem>
            <SelectItem value="tags">{t("campaigns.launchAudience.kind.tags")}</SelectItem>
            {integrationsStatus?.shopify && (
              <SelectItem value="subscribedTo">
                {t("campaigns.launchAudience.kind.subscribedTo")}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        <span className="whitespace-nowrap text-sm text-muted-foreground">{kindConfig.verb}</span>
      </div>
      {renderPicker()}
      <Button
        variant="ghost"
        size="icon"
        onClick={onRemove}
        disabled={!isRemovable}
        aria-label={
          isException
            ? t("campaigns.launchAudience.removeException")
            : t("campaigns.launchAudience.removeRule")
        }
      >
        <X />
      </Button>
    </div>
  )
}
